<template>
  <div class="input-step">
    <div class="input-step__field">
      <label class="input-step__label">{{ store.config.emailInputLabel }}</label>

      <EmailTagInput
        :tags="store.tags"
        :input-text="store.inputText"
        :placeholder="tagPlaceholder"
        :tag-count-text="tagCountText"
        @update-input-text="store.inputText = $event"
        @remove-tag="store.removeTag($event)"
        @commit="store.commitInput($event)"
      />
    </div>

    <div class="input-step__divider">
      <span class="input-step__divider-text">or</span>
    </div>

    <!-- Keyed on openGeneration so the dropzone's local UI state (drag-over,
         file-spec panel) starts fresh on every modal open. -->
    <FileDropzone
      :key="store.openGeneration"
      :file="store.inviteeFile"
      :dropzone-text="store.config.dropzoneText"
      :file-spec-html="store.config.fileSpecHtml"
      @file-selected="store.handleFileChange($event)"
      @file-removed="store.removeFile()"
    />

    <div class="input-step__field">
      <sp-switch
        class="input-step__switch"
        :checked="store.sendInvites"
        :label="`Automatically invite all ${store.labelPlural.toLowerCase()}`"
        @update-checked="handleSendInvitesChange"
      ></sp-switch>
    </div>

    <div class="input-step__error">
      {{ store.errorMessage }}
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import EmailTagInput from "./EmailTagInput.ce.vue";
import FileDropzone from "#ce/apps/SpAdmin/shared/components/FileDropzone.ce.vue";
import { useRsvpInviteStore } from "#ce/apps/SpAdmin/SpRsvpInviteApp/store/rsvpInvite";
import { pluralize } from "#ce/apps/SpAdmin/SpRsvpInviteApp/utils/emailParser";

const PLACEHOLDER_TEXT = "jane@example.com, Jane Doe <jane@example.com>";

const store = useRsvpInviteStore();

const tagPlaceholder = computed(() => (store.tags.length === 0 ? PLACEHOLDER_TEXT : ""));
const tagCountText = computed(() =>
  store.tags.length > 0 ? `${pluralize(store.tags.length, "email")} entered` : "\u00A0",
);

function handleSendInvitesChange({ detail }) {
  const [value] = detail;
  store.sendInvites = value;
}
</script>

<style scoped lang="scss">
.input-step {
  display: flex;
  flex-direction: column;

  &__field {
    display: flex;
    flex-direction: column;
    gap: var(--sp-ref-scale-8);
    margin-top: var(--sp-ref-scale-16);
  }

  &__label {
    font-size: var(--sp-sys-typescale-body-font-size, 1rem);
    font-weight: var(--sp-sys-form-field-label-font-weight, 600);
    color: var(--sp-sys-color-on-surface-variant, #666);
  }

  &__switch {
    font-weight: var(--sp-sys-form-field-label-font-weight, 600);
  }

  &__divider {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-ref-scale-6);
    padding: var(--sp-ref-scale-2) 0;
    margin-bottom: var(--sp-ref-scale-24);

    &::before,
    &::after {
      content: "";
      flex: 0 1 25%;
      height: var(--sp-ref-spacing-px);
      background-color: var(--sp-sys-color-outline, #ccc);
    }
  }

  &__divider-text {
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);
    color: var(--sp-sys-color-on-surface-variant, #666);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: var(--sp-sys-font-weight-bold, 600);
    flex-shrink: 0;
  }

  &__error {
    color: var(--sp-sys-color-error, #d32f2f);
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);
    line-height: 1.25rem;
    min-height: 1.25rem;
    margin-top: var(--sp-ref-scale-4);
  }
}
</style>
